import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { useBranding } from '../lib/branding';

const Section = ({ n, title, children }) => (
  <section className="mt-6" data-testid={`privacy-section-${n}`}>
    <h2 className="font-display text-[16px] leading-tight">
      <span className="font-mono text-neutral-400 mr-2">{String(n).padStart(2, '0')}</span>
      {title}
    </h2>
    <div className="mt-2 text-[13px] leading-relaxed text-neutral-700 space-y-2">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  const { branding } = useBranding();
  const brand = (branding.logo_text || 'PrintingKit');
  const company = (branding.powered_by || 'MACFINTI SOLUTIONS PVT LTD');
  const contactPhone = branding.contact_phone || '+91 93640 19465';
  const supportEmail = 'support@printingkit.store';
  const updated = 'February 2026';

  return (
    <div className="p-6 pb-16" data-testid="privacy-page">
      <Link to="/" className="text-[10px] tracking-[0.24em] uppercase font-bold inline-flex items-center gap-1" data-testid="privacy-back">
        <ArrowLeft size={12} /> Back to home
      </Link>

      <div className="mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Legal</div>
        <h1 className="font-display text-[30px] mt-2 leading-tight">Privacy Policy</h1>
        <p className="mt-2 text-[12px] text-neutral-500 font-mono">Last updated: {updated}</p>
      </div>

      <p className="mt-5 text-[13px] leading-relaxed text-neutral-700">
        This Privacy Policy describes how <b>{brand}</b> (operated by <b>{company}</b>) collects,
        uses, discloses and safeguards your information when you use our website
        (<a className="underline" href="https://printingkit.store">printingkit.store</a>) and mobile
        application (collectively, the "Service"). By using the Service you agree to the terms below.
        If you do not agree, please do not use the Service.
      </p>

      <Section n={1} title="Information we collect">
        <p><b>a) Information you provide:</b> name, email address, phone number, delivery address, pincode, GST number (for business orders), and any content you upload for printing (files, images, PDFs).</p>
        <p><b>b) Automatically collected:</b> device model, operating system, browser type, IP address, approximate location (derived from IP or pincode you enter), pages viewed, click events, order timings.</p>
        <p><b>c) Payment information:</b> processed by our PCI-DSS compliant payment gateway (Razorpay). We do <b>not</b> store your card number, CVV, UPI PIN or bank credentials on our servers.</p>
        <p><b>d) Location:</b> if you drag the map pin during checkout we store the chosen latitude / longitude alongside the address so our print partners can find you.</p>
      </Section>

      <Section n={2} title="How we use your information">
        <ul className="list-disc pl-5 space-y-1">
          <li>Fulfil your printing orders — routing to the nearest partner, generating invoices, coordinating pickup and delivery.</li>
          <li>Send transactional notifications (order status, payment receipts, chat messages from your assigned partner).</li>
          <li>Provide customer support and respond to enquiries.</li>
          <li>Detect fraud, prevent abuse, and enforce our Terms of Service.</li>
          <li>Improve product features, catalog and pricing (using aggregated, non-identifiable analytics).</li>
        </ul>
        <p>We <b>do not</b> use your uploaded artwork or delivery address for advertising, and we never sell your personal data to third parties.</p>
      </Section>

      <Section n={3} title="Sharing with third parties">
        <p>We share the minimum data required with the following categories of processors:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Print partners</b> — shop name, delivery address, phone (redacted until acceptance), and your uploaded artwork file. They see only the order assigned to them.</li>
          <li><b>Payment gateway (Razorpay)</b> — order amount, order id, and (during checkout) your billing name and email.</li>
          <li><b>Delivery / logistics</b> — the print partner arranges delivery locally; no third-party courier tracks you outside of the partner's own workflow.</li>
          <li><b>Cloud infrastructure</b> — MongoDB Atlas, hosted in India-region datacenters, for encrypted at-rest storage.</li>
          <li><b>Analytics</b> — PostHog and Google Fonts (see Cookies section).</li>
          <li><b>Legal</b> — we may disclose information to comply with applicable law, valid legal process, or a legitimate government request.</li>
        </ul>
      </Section>

      <Section n={4} title="Uploaded artwork files">
        <p>Files you upload for printing are stored securely, shared only with the specific print partner assigned to your order, and automatically purged from our servers <b>90 days</b> after delivery unless you have an open dispute. You retain full ownership and copyright of your artwork.</p>
      </Section>

      <Section n={5} title="Cookies & similar technologies">
        <p>We use first-party cookies to keep you logged in (<code className="text-[11px] bg-neutral-100 px-1">pk_session</code> HTTP-only cookie), and PostHog product-analytics cookies to measure aggregate feature usage. We do not use any third-party advertising cookies or cross-site trackers. You can clear cookies at any time from your browser settings; doing so will log you out.</p>
      </Section>

      <Section n={6} title="Data retention">
        <p>Orders, invoices and payment records are retained for <b>7 years</b> as required under Indian GST and income-tax law. Chat messages between you and your print partner are retained for <b>2 years</b>. Uploaded artwork is deleted after 90 days (see section 4). Account information is retained while your account is active; you can request deletion at any time (see section 8).</p>
      </Section>

      <Section n={7} title="Security">
        <p>We use HTTPS across the Service, HTTP-only + Secure cookies for session tokens, bcrypt-hashed passwords, and encryption-at-rest on our databases. No system is 100 % secure, so we recommend using a strong, unique password and enabling 2-factor authentication on your email account.</p>
      </Section>

      <Section n={8} title="Your rights">
        <p>Under India's Digital Personal Data Protection Act, 2023 you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Access a copy of your personal data.</li>
          <li>Correct inaccurate or outdated information (via your Profile page).</li>
          <li>Withdraw consent and request deletion of your account and personal data.</li>
          <li>Nominate someone to exercise these rights on your behalf.</li>
          <li>File a complaint with the Data Protection Board of India.</li>
        </ul>
        <p>To exercise any of these rights, email <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a> from the address on file. We respond within 30 days.</p>
      </Section>

      <Section n={9} title="Children">
        <p>The Service is not directed at children under 13. We do not knowingly collect data from anyone under 13; if you believe a child has provided us data, please contact us and we will delete it.</p>
      </Section>

      <Section n={10} title="Changes to this policy">
        <p>We may update this Policy from time to time. Material changes will be announced in-app and by email at least 14 days before they take effect. The "Last updated" date at the top of this page reflects the latest revision.</p>
      </Section>

      <Section n={11} title="Contact us">
        <p><b>Grievance Officer:</b> Data Protection Officer, {company}</p>
        <p><b>Email:</b> <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
        <p><b>Phone:</b> <a className="underline font-mono" href={`tel:${contactPhone.replace(/\s+/g, '')}`}>{contactPhone}</a></p>
        <p><b>Postal:</b> {company}, India</p>
      </Section>

      <div className="mt-10 text-center text-[10px] tracking-[0.22em] uppercase text-neutral-400">
        © 2026 {brand} · All rights reserved
      </div>
    </div>
  );
};

export default Privacy;
